import { type GridPaginationModel, type GridSortModel } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { type SetStateAction, useEffect, useRef } from 'react';

import { type api, getQueryProps } from 'common';

import { type RowsPerPage, useTableState } from '../components/Tables';

export interface UseCursorPaginationApiProps<TQuery extends Record<string, any>, TResponse> {
  api: {
    baseEndpoint: string;
    findAllPaginated: (
      queryParams: api.Common.Pagination.PaginatedQueryOptions<TQuery>
    ) => Promise<api.Common.Pagination.PaginationResponse<TResponse>>;
  };
  queryParams: TQuery;
  defaultPageSize?: RowsPerPage;
  enabled?: boolean;
}

function getDataGridSortToApiSortKey(sortModel: GridSortModel): string {
  return sortModel.map((sort) => `${sort.sort === 'asc' ? '' : '-'}${sort.field}`).join(',');
}

export function useCursorPaginationApi<TQuery extends Record<string, any>, TResponse>(
  props: UseCursorPaginationApiProps<TQuery, TResponse>
) {
  const { api, queryParams, defaultPageSize = 100, enabled = true } = props;

  const mapPageToNextCursor = useRef<Record<number, string>>({});
  const {
    paginationModel,
    setPaginationModel: _setPaginationModel,
    sort,
  } = useTableState({
    localStorageKeySuffix: 'CursorPagination',
    defaultPageSize,
  });

  const setPaginationModel = (paginationModelChange: SetStateAction<GridPaginationModel>) => {
    _setPaginationModel((prevPaginationModel) => {
      let newPaginationModel: GridPaginationModel;
      if (typeof paginationModelChange === 'function') {
        newPaginationModel = paginationModelChange(prevPaginationModel);
      } else {
        newPaginationModel = paginationModelChange;
      }
      if (newPaginationModel.page === 0) {
        mapPageToNextCursor.current = {};
      }
      if (
        newPaginationModel.page === 0 ||
        mapPageToNextCursor.current[newPaginationModel.page - 1]
      ) {
        return newPaginationModel;
      }
      return prevPaginationModel;
    });
  };

  const goToFirstPage = () => {
    if (paginationModel.page !== 0) {
      setPaginationModel((prevPaginationModel) => ({
        ...prevPaginationModel,
        page: 0,
      }));
    }
  };

  // The query intentionally reads from mapPageToNextCursor.current to request the cursor for
  // the previous page. These eslint overrides suppress react-hooks/refs warnings for that
  // ref access; the ref is used as mutable pagination state and does not need to trigger rerenders.
  const queryResult = useQuery({
    ...getQueryProps(
      api,
      'findAllPaginated',
      // eslint-disable-next-line react-hooks/refs
      {
        page:
          // eslint-disable-next-line react-hooks/refs
          {
            size: paginationModel.pageSize,
            // eslint-disable-next-line react-hooks/refs
            after: mapPageToNextCursor.current[paginationModel.page - 1],
          },
        ...(sort.sortModel.length ? { sort: getDataGridSortToApiSortKey(sort.sortModel) } : {}),
        ...queryParams,
      }
    ),
    enabled,
  });
  const { data } = queryResult;

  useEffect(() => {
    if (data?.meta.page.after) {
      mapPageToNextCursor.current[paginationModel.page] = data?.meta.page.after;
    }
  }, [paginationModel.page, data?.meta.page.after]);

  return {
    paginationModel,
    ...queryResult,
    goToFirstPage,
    pagination: {
      paginationModel,
      setPaginationModel,
      goToFirstPage,
    },
    sort,
  };
}
